<template>
  <div>
    <div
      v-for="(row, rowIndex) in localRows"
      :key="getRowKey(row)"
      class="rounded-md border border-light p-16 mb-16"
    >
      <div class="flex items-center justify-between mb-12">
        <div class="headline-small">
          {{ `${resolvedRowTitle} ${rowIndex + 1}` }}
        </div>
        <button
          type="button"
          class="text-sm font-medium underline text-primary-default cursor-pointer"
          :disabled="disabled"
          @click="removeRow(rowIndex)"
        >
          Ta bort rad
        </button>
      </div>

      <div
        v-for="subField in orderedSubFields"
        :key="`row-${getRowKey(row)}-sub-field-${subField.order}-${subField.label}`"
        class="mb-16"
      >
        <div class="flex items-center justify-between mb-8 text-start">
          <div class="text-sm" :class="{ 'text-error': isSubFieldInvalid(row, subField), 'text-dark': !isSubFieldInvalid(row, subField) }">
            {{ subField.label }}
            <span v-if="subField.mandatory">*</span>
          </div>
          <ion-icon
            v-if="isSubFieldInvalid(row, subField)"
            data-testid="repeater-subfield-invalid-icon"
            :icon="alertCircleOutline"
            class="w-18 h-18 text-error"
          />
        </div>

        <div
          :class="{
            'rounded-md border-2 border-error p-8': isSubFieldInvalid(row, subField) && shouldHighlightWrapper(subField.type)
          }"
        >
          <TextInput
            v-if="subField.type === FieldTypes.TEXT"
            :value="getRowValue(row, subField.label)"
            :placeholder="subField.label"
            :numeric="subField.numeric"
            :disabled="disabled"
            :invalid="isSubFieldInvalid(row, subField)"
            @on-input="(input) => setRowValue(rowIndex, subField.label, input)"
          />
          <RadioInput
            v-else-if="subField.type === FieldTypes.RADIO"
            :value="getRowValue(row, subField.label)"
            :options="subField.options"
            @on-input="(input) => setRowValue(rowIndex, subField.label, input)"
          />
          <CheckboxInput
            v-else-if="subField.type === FieldTypes.CHECKBOX"
            :value="getRowValue(row, subField.label)"
            :options="subField.options"
            @on-input="(input) => setRowValue(rowIndex, subField.label, input)"
          />
          <SelectInput
            v-else-if="subField.type === FieldTypes.SELECT"
            :options="subField.options"
            :default-value="getRowValue(row, subField.label)"
            @on-input="(input) => setRowValue(rowIndex, subField.label, input)"
          />
          <TextInput
            v-else
            :value="getRowValue(row, subField.label)"
            :placeholder="subField.label"
            :disabled="disabled"
            :invalid="isSubFieldInvalid(row, subField)"
            @on-input="(input) => setRowValue(rowIndex, subField.label, input)"
          />
        </div>
        <div v-if="subField.helptext" class="base-xs text-dark mt-8 text-start">
          {{ subField.helptext }}
        </div>
      </div>
    </div>

    <div v-if="localRows.length === 0" class="text-sm text-dark text-start mb-16">
      Inga rader tillagda
    </div>

    <button
      type="button"
      class="text-base font-medium underline text-primary-default cursor-pointer"
      :disabled="disabled"
      @click="addRow"
    >
      {{ `+ ${addRowButtonLabel}` }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { IonIcon } from '@ionic/vue';
import { alertCircleOutline } from 'ionicons/icons';
import { FieldTypes } from '@/constants/field-types';
import {
  IncidentRepeaterRow,
  RepeaterSubField
} from '@/constants/types/api';
import TextInput from '@/components/form-elements/TextInput.vue';
import RadioInput from '@/components/form-elements/RadioInput.vue';
import CheckboxInput from '@/components/form-elements/ButtonSelectInput.vue';
import SelectInput from '@/components/form-elements/SuggestionSelectInput.vue';
import { isNumericValue } from '@/utils/validation';

const LOCAL_REPEATER_ROW_KEY = '__local_repeater_row_key';

const props = withDefaults(defineProps<{
  rows?: IncidentRepeaterRow[];
  subFields?: RepeaterSubField[];
  rowTitle?: string | null;
  disabled?: boolean;
  showValidation?: boolean;
}>(), {
  rows: () => [],
  subFields: () => [],
  rowTitle: null,
  disabled: false,
  showValidation: false,
});

const emit = defineEmits<{
  (e: 'onInput', payload: IncidentRepeaterRow[]): void;
  (e: 'onAddRow'): void;
  (e: 'onRemoveRow', payload: number): void;
  (e: 'onRowValueChange', payload: { rowIndex: number; subFieldLabel: string; value: string | null }): void;
}>();

const localRows = ref<IncidentRepeaterRow[]>([]);

const orderedSubFields = computed(() => {
  return [...props.subFields].sort((left, right) => left.order - right.order);
});

const resolvedRowTitle = computed(() => {
  const normalizedTitle = getStringValue(props.rowTitle)?.trim();
  return normalizedTitle || 'Rad';
});

const addRowButtonLabel = computed(() => {
  const normalizedTitle = getStringValue(props.rowTitle)?.trim();
  const displayTitle = normalizedTitle ? normalizedTitle.toLocaleLowerCase('sv-SE') : 'rad';
  return `Lägg till ${displayTitle}`;
});

const createLocalRowKey = (): string => {
  return `local-row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

const getStringValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return value;
  }

  if (value === null || value === undefined) {
    return null;
  }

  return String(value);
};

const ensureStableRowKey = (row: IncidentRepeaterRow): IncidentRepeaterRow => {
  const hasBackendKey = typeof row.repeater_row_key === 'string' && row.repeater_row_key.trim().length > 0;
  const hasLocalKey = typeof row[LOCAL_REPEATER_ROW_KEY] === 'string' && row[LOCAL_REPEATER_ROW_KEY]?.trim().length > 0;

  if (hasBackendKey || hasLocalKey) {
    return { ...row };
  }

  return {
    ...row,
    [LOCAL_REPEATER_ROW_KEY]: createLocalRowKey(),
  };
};

const syncLocalRows = (rows: IncidentRepeaterRow[]) => {
  localRows.value = rows.map(row => ensureStableRowKey(row));
};

const emitRowsUpdate = () => {
  emit('onInput', localRows.value.map(row => ({ ...row })));
};

const getRowKey = (row: IncidentRepeaterRow): string => {
  const backendKey = getStringValue(row.repeater_row_key)?.trim();
  if (backendKey) {
    return `repeater-row-${backendKey}`;
  }

  const localKey = getStringValue(row[LOCAL_REPEATER_ROW_KEY])?.trim();
  if (localKey) {
    return `repeater-local-row-${localKey}`;
  }

  return createLocalRowKey();
};

const getRowValue = (row: IncidentRepeaterRow, label: string): string | null => {
  return getStringValue(row[label]);
};

const getTrimmedRowValue = (row: IncidentRepeaterRow, label: string): string => {
  return (getRowValue(row, label) || '').trim();
};

const isSubFieldInvalid = (row: IncidentRepeaterRow, subField: RepeaterSubField): boolean => {
  if (!props.showValidation) {
    return false;
  }

  const value = getTrimmedRowValue(row, subField.label);
  if (subField.mandatory && value.length === 0) {
    return true;
  }

  if (subField.numeric && value.length > 0 && !isNumericValue(value)) {
    return true;
  }

  return false;
};

const shouldHighlightWrapper = (subFieldType: number): boolean => {
  return subFieldType !== FieldTypes.TEXT;
};

const addRow = () => {
  if (props.disabled) {
    return;
  }

  const nextRow: IncidentRepeaterRow = {
    [LOCAL_REPEATER_ROW_KEY]: createLocalRowKey(),
  };
  orderedSubFields.value.forEach(subField => {
    nextRow[subField.label] = null;
  });

  localRows.value = [...localRows.value, nextRow];
  emit('onAddRow');
  emitRowsUpdate();
};

const removeRow = (rowIndex: number) => {
  if (props.disabled || rowIndex < 0 || rowIndex >= localRows.value.length) {
    return;
  }

  localRows.value = localRows.value.filter((_, index) => index !== rowIndex);
  emit('onRemoveRow', rowIndex);
  emitRowsUpdate();
};

const setRowValue = (rowIndex: number, subFieldLabel: string, value: unknown) => {
  if (rowIndex < 0 || rowIndex >= localRows.value.length) {
    return;
  }

  const nextValue = getStringValue(value);
  localRows.value = localRows.value.map((row, index) => {
    if (index !== rowIndex) {
      return row;
    }

    return ensureStableRowKey({
      ...row,
      [subFieldLabel]: nextValue,
    });
  });

  emit('onRowValueChange', {
    rowIndex,
    subFieldLabel,
    value: nextValue,
  });
  emitRowsUpdate();
};

watch(
  () => props.rows,
  (rows) => {
    syncLocalRows(rows);
  },
  { deep: true, immediate: true }
);
</script>
